<template>
    <div>
        <div class="tab">
            <a-tabs :active-key="activeKey" type="card-gutter" @change="tabClick">
                <a-tab-pane key="month" :title="$t('base.month')"></a-tab-pane>
                <a-tab-pane key="week" :title="$t('base.week')"></a-tab-pane>
            </a-tabs>
        </div>

        <div class="queryPanel">
            <div class="queryPanel_form" :class="{'queryPanel_form_show': showForm}">
                <Form 
                    :form="queryForm" 
                    @handleSelectChange="handleSelectChange"
                    @handlePressEnter="handlePressEnter"
                    @handleInputClear=handleInputClear
                    @handleKeydown="handleKeydown"
                ></Form>
            </div>
            <div class="queryPanel_btn">
                <a-dropdown-button size="small" @click="handle('search')">
                    <icon-search size="14" style="marginRight:5px;"/>
                    {{ $t('base.search') }}
                    <template #content>
                        <a-doption @click="handle('reset')">
                            <icon-refresh size="14" style="marginRight:5px;"/>
                            {{ $t('base.reset') }}
                        </a-doption>
                    </template>
                </a-dropdown-button>
                <span class="openBtn" v-if="!showForm" @click="showFormPanel(true)">
                    <icon-down-circle size="14"/>
                    {{ $t('base.open') }}
                </span>
                <span class="openBtn" v-else @click="showFormPanel(false)">
                    <icon-up-circle size="14" />
                    {{ $t('base.close') }}
                </span>
            </div>
        </div>

        <Table 
            class="tableList"
            ref="tableRef"
            :table="table"
            @changePage="changePage"
            @changePageSize="changePageSize"
            @filterChange="filterChange"
        >
            <template v-slot:searchFilter="{ props }">
                <div class="searchFilter">
                    <a-space direction="vertical">
                        <a-input :model-value="props.filterValue[0]" style="width:164px" @input="(value)=>props.setFilterValue([value])" />
                        <div class="searchFilter-footer">
                            <a-button style="margin-right:10px" @click="props.handleFilterReset">{{ $t('base.reset') }}</a-button>
                            <a-button type="primary" @click="props.handleFilterConfirm">{{ $t('base.confirm2') }}</a-button>
                        </div>
                    </a-space>
                </div>
            </template>

            <template v-slot:operate>
                <a-button v-permission="'setting.product.exportReportFile'" type="primary" size="mini" :disabled="exporting" @click="exportData">
                    <icon-download size="13" style="marginRight:5px;"/>
                    {{ $t('base.export') }}
                </a-button>

                <!-- userInfo.id:  86 -- Bernd Lesch,   32 -- Sophie Suen -->
                <a-button v-permission="'setting.product.placeOrder'"  v-if="activeKey == 'month' && VITE_BASE_URL && !isEdit" type="primary" status="warning" size="mini" style="margin-left:10px" @click="handle('placeOrder')">
                    <icon-edit size="13" style="marginRight:5px;"/>
                    {{ $t('product.placeOrder') }}  
                </a-button>

                <a-button v-permission="'setting.product.placeOrder'" v-if="activeKey == 'month' && VITE_BASE_URL && isEdit" type="primary" status="success" size="mini" style="margin-left:10px" @click="handle('save')">
                    <icon-save size="13" style="marginRight:5px;"/>
                    {{ $t('base.save') }}
                </a-button>

                <a-button v-permission="'setting.product.placeOrder'" v-if="activeKey == 'month' && VITE_BASE_URL && isEdit" type="outline" status="warning" size="mini" style="margin-left:10px" @click="handle('cancelPlaceOrder')">
                    <icon-undo size="13" style="marginRight:5px;"/>
                    {{ $t('base.cancel') }}
                </a-button>

                <a-button v-permission="'setting.product.confirmPlaceOrder'" v-if="activeKey == 'month' && VITE_BASE_URL_2" type="primary" size="mini" :disabled="isEdit" style="margin-left:10px" @click="handle('confirm')">
                    <icon-check size="13" style="marginRight:5px;"/>
                    {{ $t('base.confirm2') }}
                </a-button>
                
            </template>

            <template v-slot:columns="{ props }">
                <div v-if="isEdit">
                    <div v-if="props.column.key == 'place_order_quantity_total'">
                        <a-input-number 
                            v-model="props.record.place_order_quantity_total" 
                            :style="{width:'80px'}"
                            :min="0"
                            hide-button
                            @input="(val)=>changeInputNumber(val, props.record)"
                        />
                    </div>
                    <div v-if="props.column.key == 'place_order_plan_eta'">
                        <a-date-picker 
                            v-model="props.record.place_order_plan_eta" 
                            style="width: 150px;" 
                            :disabledDate="(current) => dayjs(current).isBefore(dayjs())"
                        />
                    </div>
                </div>
                <div v-else>
                    <div v-if="props.column.key == 'place_order_quantity_total'">
                        {{ props.record.place_order_quantity_total }}
                    </div>
                    <div v-if="props.column.key == 'year_end_unshippable' ">
                        {{ props.record.year_end_unshippable }}
                    </div>
                    <div v-if="props.column.key == 'place_order_plan_eta'">
                        {{ props.record.place_order_plan_eta }}
                    </div>
                </div>
                
            </template>

        </Table>
    </div>
</template>

<script setup>
    import { onMounted, reactive, ref, watch, nextTick, h } from 'vue';
    import { ProductApi } from '@/api/ProductApi.js';
    import { OrderApi } from '@/api/OrderApi.js'; 
    import { Message, Modal } from '@arco-design/web-vue';
    import Table from '@/components/Table.vue';
    import Form from '@/components/Form.vue';
    import { AvailabilityReportQuery } from './queryConfig/AvailabilityReportQuery.js';
    import { useI18n } from 'vue-i18n';
    import { useLocale } from '@/store/index.js';
    import { dateToTimestamp, timestampToDate, formatterToPoint, formatterToComma, getNowDate, getWeek, getYearWeek } from '@/utils/index.js';
    import { IconSearch } from '@arco-design/web-vue/es/icon';
    import dayjs from 'dayjs';

    const localeStore = useLocale()

    const { t } = useI18n()

    // userInfo.id:  86 -- Bernd Lesch,   32 -- Sophie Suen
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo'))
    const VITE_BASE_URL = import.meta.env.VITE_BASE_URL == "https://oms.hepha.com/hephaOrder"? userInfo.id == 86 || userInfo.id == 32 : true
    const VITE_BASE_URL_2 = import.meta.env.VITE_BASE_URL == "https://oms.hepha.com/hephaOrder"? userInfo.id == 32 : true
    
    const queryForm = reactive(AvailabilityReportQuery)
    const showForm = ref(false)
    const activeKey = ref('month')
    const rowData = ref(null)
    const operateType = ref('add')
    const exporting = ref(false)
    const isEdit = ref(false)
    const weekRange = ref([])
    const factoryArr = ref([])


    const currentMonth = ref(dayjs().month() + 1);
    const yearColumns = ref([
            {
                title: '#',
                dataIndex: 'No',
                key:'No',
                width:'100',
                fixed:'left',
                align:'left',
            },
            {
                // title: 'model',
                title: t('product.model'),
                // width:'170',
                ellipsis:true,
                tooltip:true,  
                align:'center',
                children:[
                    {
                        title: t('product.modelYear'),
                        dataIndex: 'model_year',
                        key:'model_year',
                        width:'130',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.model_year) 
                            },
                        }
                    },
                    {
                        title: t('product.model'),
                        dataIndex: 'model',
                        key:'model',
                        width:'200',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.model) 
                            },
                        }
                    },
                    {
                        title: t('product.sku'),
                        dataIndex: 'sku',
                        key:'sku',
                        width:'170',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filter: (value, record) => record.sku.includes(value),
                            slotName: 'searchFilter',
                            icon: () => h(IconSearch)
                        }
                    },
                    {
                        title: t('product.version'),
                        dataIndex: 'hf_version',
                        key:'hf_version',
                        width:'120',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.hf_version) 
                            },
                        }
                    },
                    {
                        title: t('product.frameColor2'),
                        dataIndex: 'frame_color',
                        key:'frame_color',
                        width:'140',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.frame_color) 
                            },
                        }
                    },
                    {
                        title: t('product.frameType2'),
                        dataIndex: 'frame_shape',
                        key:'frame_shape',
                        width:'140',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.frame_shape) 
                            },
                        }
                    },
                    {
                        title: t('product.frameSize2'),
                        dataIndex: 'frame_size',
                        key:'frame_size',
                        width:'140',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.frame_size) 
                            },
                        }
                    },
                ]
            },
            {
                title: t('product.stock'),
                // width:'140',
                ellipsis:true,
                tooltip:true,
                align:'center',
                children:[
                    {
                        title: t('product.zu'),
                        dataIndex: 'zu_inventory',
                        key:'zu_inventory',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                    {
                        title: t('product.maisach'),
                        dataIndex: 'hepha_inventory',
                        key:'hepha_inventory',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                    {
                        title: t('product.cwh'),
                        dataIndex: 'cwh_inventory',
                        key:'cwh_inventory',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                    {
                        title: t('product.TTL'),
                        dataIndex: 'inventory_by_warehouse_total',
                        key:'inventory_by_warehouse_total',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [{
                                text: '> 0',
                                value: '0',
                            }],
                            filter: (value, row) => row.inventory_by_warehouse_total > 0,
                        }
                    },
                    {
                        title: t('product.transit'),
                        dataIndex: 'transit_quantity_total',
                        key:'transit_quantity_total',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                ]
            },
            {
                // title: '年度',
                title: '2025',
                // width:'150',
                dataIndex:'year',
                key:'year',
                ellipsis:true,
                tooltip:true,
                align:'center',
                children: [
                    ...Array.from({ length: 12 }, (_, i) => {
                        const month = i + 1;
                        let columnChildren = [];
                        
                        const currentMonth = dayjs().month() + 1;
                        
                        if (month < currentMonth) {
                            return {
                                title: t(`product.${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1]}`),
                                key: `month_${month}`,
                                align: 'center',
                                dataIndex: `current_year_month_plan_map.${month}.past_month_qty`,
                                width: '80'
                            };
                        } else if (month === currentMonth) {
                            columnChildren = [
                                {
                                    title: t('product.toBeDelivered'),
                                    dataIndex: `current_year_month_plan_map.${month}.to_be_delivered`,
                                    key: `to_be_delivered_${month}`,
                                    width: '130',
                                    align: 'center'
                                },
                                {
                                    title: t('product.deliveryPlan'),
                                    dataIndex: `current_year_month_plan_map.${month}.delivery_plan`,
                                    key: `delivery_plan_${month}`,
                                    width: '130',
                                    align: 'center'
                                },
                                {
                                    title: t('product.supply'),
                                    dataIndex: `current_year_month_plan_map.${month}.supply`,
                                    key: `supply_${month}`,
                                    width: '130',
                                    align: 'center'
                                },
                                {
                                    title: t('product.endOfMonthInventory'),
                                    dataIndex: `current_year_month_plan_map.${month}.end_of_month_inventory`,
                                    key: `end_of_month_inventory_${month}`,
                                    width: '130',
                                    align: 'center'
                                },
                                {
                                    title: t('product.overdueUnavailable'),
                                    dataIndex: `current_year_month_plan_map.${month}.overdue_unavailable`,
                                    key: `overdue_unavailable_${month}`,
                                    width: '130',
                                    align: 'center'
                                }
                            ];
                        } else {
                            columnChildren = [
                                {
                                    title: t('product.deliveryPlan'),
                                    dataIndex: `current_year_month_plan_map.${month}.delivery_plan`,
                                    key: `delivery_plan_${month}`,
                                    width: '130',
                                    align: 'center'
                                },
                                {
                                    title: t('product.supply'),
                                    dataIndex: `current_year_month_plan_map.${month}.supply`,
                                    key: `supply_${month}`,
                                    width: '130',
                                    align: 'center'
                                },
                                {
                                    title: t('product.endOfMonthInventory'),
                                    dataIndex: `current_year_month_plan_map.${month}.end_of_month_inventory`,
                                    key: `end_of_month_inventory_${month}`,
                                    width: '130',
                                    align: 'center'
                                }
                            ];
                        }

                        return {
                            title: t(`product.${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1]}`),
                            key: `month_${month}`,
                            align: 'center',
                            children: columnChildren
                        };
                    }),
                    {
                        title: t('product.TTL'),
                        dataIndex:  `current_year_month_plan_map.0.supply`,
                        key: `month_0`,
                        width: '140',
                        ellipsis: true,
                        tooltip: true,
                        align: 'center',
                        filterable: {
                            filters: [{ text: '> 0', value: '0' }],
                            filter: (value, row) => row.current_year_month_plan_map[0].supply > 0
                        }
                    }
                ]
            },
            {
                title: t('product.yearEndUnshippable'),
                dataIndex: 'year_end_unshippable',
                key:'year_end_unshippable',
                width:'120',
                ellipsis:false,
                tooltip:true,
                align:'center',
                slotName:'slot',
                filterable: {
                    filters: [{
                        text: '< 0',
                        value: '0',
                    }],
                    filter: (value, row) => row.year_end_unshippable < 0,
                }
            },
            {
                title: t('product.TTL'),
                dataIndex: 'plan_add_inventory_add_transit_quantity_total',
                key:'plan_add_inventory_add_transit_quantity_total',
                width:'120',
                ellipsis:true,
                tooltip:true,
                align:'center',
                filterable: {
                    filters: [{
                        text: '> 0',
                        value: '0',
                    }],
                    filter: (value, row) => row.plan_add_inventory_add_transit_quantity_total > 0,
                }
            },
            {
                title: t('product.quantity'),
                dataIndex: 'place_order_quantity_total',
                key:'place_order_quantity_total',
                width:'120',
                ellipsis:true,
                tooltip:true,
                align:'center',
                slotName:'slot'
            },
            {
                title: t('product.expectETADate'),
                dataIndex: 'place_order_plan_eta',
                key:'place_order_plan_eta',
                width:'190',
                ellipsis:true,
                tooltip:true,
                align:'center',
                slotName:'slot'
            },
    ])
    const weekColumns = ref([
            {
                title: '#',
                dataIndex: 'No',
                key:'No',
                width:'100',
                fixed:'left',
                align:'left',
            },
            {
                title: t('product.model'),
                // width:'170',
                ellipsis:true,
                tooltip:true,  
                align:'center',
                children:[
                    {
                        title: t('product.modelYear'),
                        dataIndex: 'model_year',
                        key:'model_year',
                        width:'130',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.model_year) 
                            },
                        }
                    },
                    {
                        title: t('product.model'),
                        dataIndex: 'model',
                        key:'model',
                        width:'200',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.model) 
                            },
                        }
                    },
                    {
                        title: t('product.sku'),
                        dataIndex: 'sku',
                        key:'sku',
                        width:'170',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filter: (value, record) => record.sku.includes(value),
                            slotName: 'searchFilter',
                            icon: () => h(IconSearch)
                        }
                    },
                    {
                        title: t('product.version'),
                        dataIndex: 'hf_version',
                        key:'hf_version',
                        width:'120',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.hf_version) 
                            },
                        }
                    },
                    {
                        title: t('product.frameColor2'),
                        dataIndex: 'frame_color',
                        key:'frame_color',
                        width:'140',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.frame_color) 
                            },
                        }
                    },
                    {
                        title: t('product.frameType2'),
                        dataIndex: 'frame_shape',
                        key:'frame_shape',
                        width:'140',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.frame_shape) 
                            },
                        }
                    },
                    {
                        title: t('product.frameSize2'),
                        dataIndex: 'frame_size',
                        key:'frame_size',
                        width:'140',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [],
                            multiple: true,
                            filter: (value, row) =>{
                                return value.includes(row.frame_size) 
                            },
                        }
                    },
                ]
            },
            {
                title: t('product.stock'),
                // width:'140',
                ellipsis:true,
                tooltip:true,
                align:'center',
                children:[
                    {
                        title: t('product.zu'),
                        dataIndex: 'zu_inventory',
                        key:'zu_inventory',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                    {
                        title: t('product.maisach'),
                        dataIndex: 'hepha_inventory',
                        key:'hepha_inventory',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                    {
                        title: t('product.cwh'),
                        dataIndex: 'cwh_inventory',
                        key:'cwh_inventory',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                    {
                        title: t('product.TTL'),
                        dataIndex: 'inventory_by_warehouse_total',
                        key:'inventory_by_warehouse_total',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                        filterable: {
                            filters: [{
                                text: '> 0',
                                value: '0',
                            }],
                            filter: (value, row) => row.inventory_by_warehouse_total > 0,
                        }
                    },
                    {
                        title: t('product.transit'),
                        dataIndex: 'transit_quantity_total',
                        key:'transit_quantity_total',
                        width:'100',
                        ellipsis:true,
                        tooltip:true,
                        align:'center',
                    },
                ]
            },
            {
                title: '2025',
                // width:'150',
                dataIndex:'year',
                key:'year',
                ellipsis:true,
                tooltip:true,
                align:'center',
                children:[
                    // {
                    //     title: t('product.week'),
                    //     dataIndex: '1',
                    //     key:'1',
                    //     width:'100',
                    //     ellipsis:true,
                    //     tooltip:true,
                    //     align:'center',
                    // },
                    // {
                    //     title: t('product.TTL'),
                    //     dataIndex: '0',
                    //     key:'0',
                    //     width:'100',
                    //     ellipsis:true,
                    //     tooltip:true,
                    //     align:'center',
                    // },
                ],
            },
            {
                title: t('product.TTL'),
                dataIndex: 'plan_add_inventory_add_transit_quantity_total',
                key:'plan_add_inventory_add_transit_quantity_total',
                width:'120',
                ellipsis:true,
                tooltip:true,
                align:'center',
                filterable: {
                    filters: [{
                        text: '> 0',
                        value: '0',
                    }],
                    filter: (value, row) => row.plan_add_inventory_add_transit_quantity_total > 0,
                }
            },
            // {
            //     title: t('order.quantity'),
            //     dataIndex: 'place_order_quantity_total',
            //     key:'place_order_quantity_total',
            //     width:'150',
            //     ellipsis:true,
            //     tooltip:true,
            //     align:'center',
            //     slotName:'slot'
            // },
            // {
            //     title: t('plan.recordDate'),
            //     dataIndex: 'place_order_plan_eta',
            //     key:'place_order_plan_eta',
            //     width:'150',
            //     ellipsis:true,
            //     tooltip:true,
            //     align:'center',
            //     slotName:'slot'
            // },
    ])
    const table = ref({
        datas:[],
        columns:[],
        configs:{
            scroll:{
                x:1000,
                y:530,
            },
            stripe:true,
            bordered:false,
            size:'large',
            loading:false,
            summary:true,
            summaryText: t('base.total'),
            expandable:'',
            expand:false,
            pagination:false,
            // dataSpanMethod:({record, column, rowIndex, columnIndex})=>{
            //     console.log({record, column, rowIndex, columnIndex},'kkkk')
            //     let indexArr = [1, 2, 3, 4, 5 , 6, 7]
            //     console.log(indexArr.includes(columnIndex) && rowIndex == table.value.datas.length - 1, 'kkoopp')
            //     if (indexArr.includes(columnIndex) && rowIndex == table.value.datas.length - 1) {
            //         return {colspan: 7}
            //     }
            // },
        },
    })

    const handle = (type, row)=>{
        switch(type){
            case 'search':
                if(!queryForm.formData.begin_week){
                    Message.error(t('base.start_week')+' '+t('base.noEmpty'))
                    return
                }else if(!queryForm.formData.end_week){
                    Message.error(t('base.end_week')+' '+t('base.noEmpty'))
                    return
                }else if(queryForm.formData.end_week < queryForm.formData.begin_week){
                    Message.error(t('product.endWeekTip'))
                    return
                }

                if(factoryArr.value[0] == '') factoryArr.value.shift()
                queryForm.formData.factors = factoryArr.value

                table.value.configs.loading = true
                if(activeKey.value == 'week'){
                    setWeekCol()
                }
                getList()
                break;
            case 'reset':
                queryForm.formData.year = new Date().getUTCFullYear()
                queryForm.formData.factors = ''
                if(activeKey.value == 'week'){
                    queryForm.formData.begin_week = 1 
                    queryForm.formData.end_week = getYearWeek(getNowDate())
                }
                getList()
                break;
            case 'placeOrder':
                isEdit.value = true
                getList()
                break;
            case 'save':
                saveQtyPlanETA()
                break;
            case 'cancelPlaceOrder':
                isEdit.value = false
                break;
            case 'confirm':
                Modal.confirm({
                    title: t('base.tip'),
                    titleAlign:'start',
                    content:t('base.isConfirmTip'),
                    okText:t('base.ok'),
                    cancelText:t('base.cancel'),
                    onOk:()=>{
                        confirmQtyPlanETA()
                    },
                })
                break;
        }
    }

    const init = ()=>{
        queryForm.formData.year = new Date().getUTCFullYear()
        queryForm.formData.begin_week = 1
        queryForm.formData.end_week = getYearWeek(getNowDate())

        queryForm.formField.forEach(i=>{
            if(i.key == 'begin_week') i.show = activeKey.value == 'week'
            if(i.key == 'end_week') i.show = activeKey.value == 'week'
        })

        table.value.columns = yearColumns.value

        getList()
    }

    const tabClick = (val)=>{
        activeKey.value = val
        isEdit.value = false
        
        queryForm.formField.forEach(i=>{
            if(i.key == 'begin_week') i.show = val == 'week'
            if(i.key == 'end_week') i.show = val == 'week'
        })

        if(val == 'month'){
            table.value.columns = yearColumns.value
        }else{
            setWeekCol()
        }

        getList()
    }

    const showFormPanel = (val)=>{
        showForm.value = val
    }

    // 设置动态周别
    const setWeekCol = ()=>{
        weekColumns.value[3].children = []
        weekRange.value = []

        if(queryForm.formData.begin_week == queryForm.formData.end_week){
            weekColumns.value[3].children.push({
                title: `${t('base.week')} ${queryForm.formData.begin_week}`,
                dataIndex: `${queryForm.formData.begin_week}`,
                key:`${queryForm.formData.begin_week}`,
                width:'100',
                ellipsis:true,
                tooltip:true,
                align:'center',
            })
            weekRange.value.push(queryForm.formData.begin_week)
        }else{
            for(let i=queryForm.formData.begin_week; i<=queryForm.formData.end_week; i++){
                weekColumns.value[3].children.push({
                    title: `${t('base.week')} ${i}`,
                    dataIndex: `${i}`,
                    key:`${i}`,
                    width:'100',
                    ellipsis:true,
                    tooltip:true,
                    align:'center',
                })
                weekRange.value.push(i)
            }
        }

        weekColumns.value[3].children.push({
            title: t('product.TTL'),
            dataIndex: '0',
            key:'0',
            width:'100',
            ellipsis:true,
            tooltip:true,
            align:'center',
            filterable: {
                filters: [{
                    text: '> 0',
                    value: '0',
                }],
                filter: (value, row) => row['0'] > 0,
            }
        })

        table.value.columns = weekColumns.value
    }

    // 导出
    const exportData = async()=>{
        Message.success(t('base.exporting'))
        table.value.configs.loading = true
        let query_type = activeKey.value == 'month'? 'MONTH' : 'WEEK'
        let res = activeKey.value == 'month'
                  ? await ProductApi.getInstance().exportEBikeReportByMonth(queryForm.formData.year, query_type, queryForm.formData.factors)
                  : await ProductApi.getInstance().exportEBikeReportByWeek(queryForm.formData.year, query_type, queryForm.formData.begin_week, queryForm.formData.end_week, queryForm.formData.factors)

        if(!res.code){
            let blob = new Blob([res])
            let a = document.createElement('a')    
            a.href = URL.createObjectURL(blob)
            a.download = `${t('product.availabilityReportData')}.xlsx`
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            URL.revokeObjectURL(a.href)
            document.body.removeChild(a)
            Message.success(t('base.exportSuccess'))
            table.value.configs.loading = false
        }else{  
            Message.error(res.msg)
        }
    }

    const filterChange = (dataIndex,filteredValues)=>{
        console.log(dataIndex,filteredValues,'dataIndex,filteredValues')
    }

    const changePage = (current)=>{
        table.value.configs.pagination.current = current
        getList()
    }

    const changePageSize = (pageSize)=>{
        table.value.configs.pagination.pageSize = pageSize
        getList()
    }

    // 列表查询-input
    const handleSelectChange = (val,item, key)=>{
        console.log(val, item, key, 'item, opt, val')
        if(key == 'year'){
            let year_item = yearColumns.value.find(i=> i.key == 'year')
            let week_item = weekColumns.value.find(i=> i.key == 'year')
            let year_child = yearColumns.value.find(i=> i.key == 'year')
            let end_unshippable = yearColumns.value.find(i=> i.key == 'year_end_unshippable')
            year_item.title = val
            week_item.title = val
            if(val == 2026){
                // end_unshippable.dataIndex = 'year_end_unshippable_next_year',
                // end_unshippable.key = 'year_end_unshippable_next_year',
                end_unshippable.filterable = {
                    filters: [{
                        text: '< 0',
                        value: '0',
                    }],
                    filter: (value, row) => row.year_end_unshippable < 0,
                }
                year_child.children = [
                    {
                        title: t('product.initialInventory'),
                        dataIndex: 'initial_inventory',
                        key: 'initial_inventory',
                        width: '100',
                        ellipsis: false,
                        tooltip: true,
                        align: 'center',
                        filterable: {
                            filters: [
                            {
                                text: '≠ 0',
                                value: '0',
                            },
                            {
                                text: '> 0',
                                value: '1',
                            },
                            {
                                text: '< 0',
                                value: '2',
                            }],
                            filter: (value, row) => {
                                if (value == '1') {
                                    return row.initial_inventory > 0;
                                } else if (value == '2') {
                                    return row.initial_inventory < 0;
                                } else if (value == '0') {
                                    return row.initial_inventory != 0;
                                }
                                return true;
                            }
                        }
                    },
                    ...Array.from({ length: 12 }, (_, i) => {
                        const month = i + 1;
                        let columnChildren = [];
                        columnChildren = [
                            {
                                title: t('product.deliveryPlan'),
                                dataIndex: `next_year_month_plan_map.${month}.delivery_plan`,
                                key: `delivery_plan_${month}`,
                                width: '130',
                                align: 'center'
                            },
                            {
                                title: t('product.supply'),
                                dataIndex: `next_year_month_plan_map.${month}.supply`,
                                key: `supply_${month}`,
                                width: '130',
                                align: 'center'
                            },
                            {
                                title: t('product.endOfMonthInventory'),
                                dataIndex: `next_year_month_plan_map.${month}.end_of_month_inventory`,
                                key: `end_of_month_inventory_${month}`,
                                width: '130',
                                align: 'center'
                            }
                        ];
                        
                        return {
                            title: t(`product.${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1]}`),
                            key: `month_${month}`,
                            align: 'center',
                            children: columnChildren
                        };
                    }),
                    {
                        title: t('product.TTL'),
                        dataIndex: 'next_year_month_plan_map.0.supply',
                        key: '0',
                        width: '100',
                        ellipsis: true,
                        tooltip: true,
                        align: 'center',
                        filterable: {
                            filters: [{ text: '> 0', value: '0' }],
                            filter: (value, row) => row.next_year_month_plan_map[0].supply > 0
                        }
                    }
                ]
            }
        
            getList()
        }
        if(key == 'factors'){
            factoryArr.value = val
        }
        
    }
    // 列表查询-input
    const handleInputClear = (val,item)=>{
        table.value.configs.loading = true
        if(activeKey.value == 'week'){
            setWeekCol()
        }
        getList()
    }
    const handleKeydown = (val)=>{
        if(val == 13){
            if(!queryForm.formData.begin_week){
                Message.error(t('base.start_week')+' '+t('base.noEmpty'))
                return
            }else if(!queryForm.formData.end_week){
                Message.error(t('base.end_week')+' '+t('base.noEmpty'))
                return
            }else if(queryForm.formData.end_week < queryForm.formData.begin_week){
                Message.error(t('product.endWeekTip'))
                return
            }

            table.value.configs.loading = true

            if(activeKey.value == 'week'){
                setWeekCol()
            }
            getList()
        }
    }

    const changeInputNumber = (val, row)=>{
        console.log(val, 'changeInputNumber', row)
        if(val == 0) row.place_order_plan_eta = ''
    }
    
    const formatValues = (data, field)=>{
        return data[field] === null || data[field] === undefined? 0 : data[field]
    }

    // 获取计划列表
    const getList = async()=>{
        let query_type = activeKey.value == 'month'? 'MONTH' : 'WEEK'
        table.value.configs.loading = true
        let res = activeKey.value == 'month'
                ? await ProductApi.getInstance().getEBikeReportListByMonth(queryForm.formData.year, query_type, queryForm.formData.factors)
                : await ProductApi.getInstance().getEBikeReportListByWeek(queryForm.formData.year, query_type, queryForm.formData.begin_week, queryForm.formData.end_week, queryForm.formData.factors)
        
        if(res.code == 0){
            // table.value.datas = formatEmptyValues(res.data);
            let model_year_arr = [],  model_arr = [], version_arr = [], color_arr = [], type_arr = [], size_arr = []
            let current_year = new Date().getFullYear()
            table.value.datas = []
            res.data.pop()
            res.data.length > 0 &&  res.data.forEach((i,index)=>{
                i = Object.assign(i, i.plan_map)
                if(activeKey.value == 'month'){
                    for(let j=0; j<=12; j++){
                        i[`${j}`] = i[`${j}`] == undefined? 0 : i[`${j}`]
                    }
                }else{
                    weekRange.value.forEach(m=>{
                        i[`${m}`] = i[`${m}`] == undefined? 0 : i[`${m}`]
                    })
                    i['0'] = i['0'] == undefined? 0 : i['0']
                }

                if(!model_year_arr.includes(i.model_year)){
                    model_year_arr.push(i.model_year)
                }
                if(!model_arr.includes(i.model)){
                    model_arr.push(i.model)
                }
                if(!version_arr.includes(i.hf_version)){
                    version_arr.push(i.hf_version)
                }
                if(!color_arr.includes(i.frame_color)){
                    color_arr.push(i.frame_color)
                }
                if(!type_arr.includes(i.frame_shape)){
                    type_arr.push(i.frame_shape)
                }
                if(!size_arr.includes(i.frame_size)){
                    size_arr.push(i.frame_size)
                }

                yearColumns.value[1].children.forEach(i=>{
                    if(i.key == 'model_year'){
                        i.filterable.filters = []
                        model_year_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'model'){
                        i.filterable.filters = []
                        model_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'hf_version'){
                        i.filterable.filters = []
                        version_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'frame_color'){
                        i.filterable.filters = []
                        color_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'frame_shape'){
                        i.filterable.filters = []
                        type_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'frame_size'){
                        i.filterable.filters = []
                        size_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                })

                weekColumns.value[1].children.forEach(i=>{
                    if(i.key == 'model_year'){
                        i.filterable.filters = []
                        model_year_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'model'){
                        i.filterable.filters = []
                        model_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'hf_version'){
                        i.filterable.filters = []
                        version_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'frame_color'){
                        i.filterable.filters = []
                        color_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'frame_shape'){
                        i.filterable.filters = []
                        type_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                    if(i.key == 'frame_size'){
                        i.filterable.filters = []
                        size_arr.forEach(m=>{
                            i.filterable.filters.push({
                                text: m,
                                value: m,
                            })
                        })
                    } 
                })

                // console.log(model_year_arr,'model_year_arr', model_arr)
                let monthPlanMap = []
                if(activeKey.value == 'month'){
                    monthPlanMap = queryForm.formData.year == current_year? [i.current_year_month_plan_map[0]] : [i.next_year_month_plan_map[0]]
                    for(let j=1; j<=12; j++){
                        if(j < currentMonth.value){
                            monthPlanMap.push({
                                month: j,
                                past_month_qty: 0,
                            })
                        }else{
                            queryForm.formData.year == current_year? monthPlanMap.push(i.current_year_month_plan_map[j]) : monthPlanMap.push(i.next_year_month_plan_map[j])
                        }
                    }
                }
                

                table.value.datas.push({
                    ...i,
                    No: index + 1,
                    model_year: i.model_year == undefined? '-' : i.model_year,
                    model: i.model == undefined? '-' : i.model,
                    sku: i.sku == undefined? '-' : i.sku,
                    hf_version: i.hf_version == undefined? '-' : i.hf_version,
                    frame_color: i.frame_color == undefined? '-' : i.frame_color,
                    frame_shape: i.frame_shape == undefined? '-' : i.frame_shape,
                    frame_size: i.frame_size == undefined? '-' : i.frame_size,
                    inventory_by_warehouse_total: formatValues(i, 'inventory_by_warehouse_total'), //i.inventory_by_warehouse_total == undefined? 0 : i.inventory_by_warehouse_total,
                    transit_quantity_total: formatValues(i, 'transit_quantity_total'), // i.transit_quantity_total == undefined? 0 : i.transit_quantity_total,
                    place_order_quantity_total: formatValues(i, 'place_order_quantity_total'), // i.place_order_quantity_total == undefined? 0 : i.place_order_quantity_total,
                    year_end_unshippable: queryForm.formData.year == current_year? formatValues(i, 'year_end_unshippable_current_year') : formatValues(i, 'year_end_unshippable_next_year'),
                    place_order_plan_eta: i.place_order_plan_eta == undefined? '-' : i.place_order_plan_eta,
                    current_year_month_plan_map: monthPlanMap,
                })
            })
            table.value.configs.loading = false
        }else{  
            Message.error(res.msg)
        }
    }

    // 保存 数量和交期
    const saveQtyPlanETA = ()=>{
        let param = []
        for(let i=0; i<table.value.datas.length; i++){
            let item = table.value.datas[i]

            console.log(item.place_order_quantity_total, !item.place_order_plan_eta, item.place_order_quantity_total != 0 && item.place_order_quantity_total != undefined && !item.place_order_plan_eta, 'ssss')

            if(item.place_order_quantity_total != 0 && item.place_order_quantity_total != undefined && !item.place_order_plan_eta){
                Message.error(t('plan.recordDate') + ' ' + t('base.noEmpty'))
                return
            }else if(item.place_order_quantity_total == undefined && item.place_order_plan_eta){
                Message.error(t('order.quantity') + ' ' + t('base.noEmpty'))
                return
            }

            if(item.place_order_quantity_total != undefined && !!item.place_order_plan_eta){
                param.push({
                    sku: item.sku,
                    quantity: item.place_order_quantity_total,
                    plan_eta: item.place_order_plan_eta,
                })
            }
        }
        console.log(param,'param')
        
        Modal.confirm({
            title: t('base.tip'),
            titleAlign:'start',
            content:t('base.saveTip'),
            okText:t('base.ok'),
            cancelText:t('base.cancel'),
            onOk:async ()=>{
                let res = await ProductApi.getInstance().saveQtyPlanETA(param)
                if(res.code == 0){
                    isEdit.value = false
                    Message.success(t('base.saveSuccess'))
                    getList()
                }else{
                    Message.error(res.msg)
                }    
            },
        })
        
    }

    // 确认 数量和交期
    const confirmQtyPlanETA = async ()=>{
        let res = await ProductApi.getInstance().confirmQtyPlanETA()
        if(res.code == 0){
            Message.success(t('base.confirmSuccess'))
            getList()
        }else{
            Message.error(res.msg)
        }
    }

    // 处理查询面板伸缩时表格高度
    const resetTableHeight = (clientHeight)=>{
        const headerHeight = document.querySelector('.arco-layout-header').clientHeight
        const breadcrumbHeight = document.querySelector('.arco-page-header-with-breadcrumb').clientHeight
        const searchContentDomHeight = document.querySelector('.queryPanel').clientHeight
        const pageHeight = 0
        const footerHeight = document.querySelector('.footer').clientHeight
        table.value.configs.scroll.y = clientHeight - headerHeight - breadcrumbHeight - searchContentDomHeight - pageHeight - footerHeight - 250
    }

    watch(localeStore,(val)=>{
        resetTableHeight(val.clientHeight)
    })

    onMounted(()=>{
        init()

        nextTick(()=>{
            const clientHeight = document.body.clientHeight
            resetTableHeight(clientHeight)
        })
    })

</script>

<style lang="less" scoped>
.tab{
    background: #fff;
}

/deep/ .arco-tabs-content {
    padding-top: 0;
}

/deep/ .arco-tabs-tab-active,
/deep/ .arco-tabs-tab-active:hover{
    background: #165dff;
    color: #fff;
}

.queryPanel{
    display: flex;
    // margin-bottom:7px;
    padding:7px;
    background: #fff;
    border-bottom: 1px dashed #ccc;

    .queryPanel_form{
        display: flex;
        flex:14;
        flex-direction: column;
        height: 33px;
        overflow: hidden;

        /deep/ .arco-form-layout-inline .arco-form-item{
            margin-bottom:0;
        }
    }

    .queryPanel_form_show{
        height: auto;
        overflow: visible;
    }

    .queryPanel_btn{
        display: flex;
        flex:1;
        // flex-direction: column;
        justify-content: center;
        align-items: center;
        border-left:1px solid #eee;
        padding-left:5px;

        .openBtn{
            display: inline-block;
            padding: 3px 10px;
            margin: 2px 5px 0 5px;
            width: 80px;
            text-align: center;
            color: #165dff;
            cursor: pointer;
            border: 1px solid #165dff;
            border-radius:2px;
        }
    }

    /deep/ .arco-form-item-label-col > .arco-form-item-label{
        font-size:12px;
        margin-left:20px;
    }

    /deep/ .arco-form-item-layout-inline{
        margin-right: 0;
    }

    .form_btn{
        margin:5px 5px 3px;
        width:70px;
    }
}

/deep/ .operayeBox {
    margin-bottom: 7px;
    background: #fff;
    padding: 5px 24px 5px;
}

/deep/ .arco-btn-group{
    vertical-align: bottom;
}
/deep/ .arco-btn-group .arco-btn:first-child,
/deep/ .arco-btn-group .arco-btn:last-child{
    background: #165dff;
    color: #fff;
}

// 表格
/deep/ .arco-table-td{
    padding: 5px 0;
    border-right: 1px solid #eee;
}

/deep/ .arco-table-container{
    padding-bottom:0;
}

/deep/ .arco-picker-size-medium input,
/deep/ .arco-input-wrapper .arco-input.arco-input-size-medium,
/deep/ .arco-select-view-single.arco-select-view-size-medium .arco-select-view-input{
    font-size:12px;
}

/deep/ .arco-pagination-options .arco-select,
/deep/ .arco-pagination-jumper .arco-pagination-jumper-input input{
    background: #e7e7e7;
}

/deep/  button:focus,
/deep/  button:focus-visible{
    outline: none;
}

.tableList{
    /deep/ .arco-table-th{
        background: #ffd681;
        border-bottom: 1px solid #eee;
        border-right: 1px solid #eee;
        font-size: 13px;
        font-weight: 600;
    }

    /deep/ .arco-table .arco-table-cell{
        padding: 5px 16px;
    }
    
    /deep/ .arco-table-tr:nth-of-type(1){
        .arco-table-th:nth-of-type(1){
            background: #fff;
        }

        .arco-table-th:nth-of-type(2){
            background: #ccc;
        }

        .arco-table-th:nth-of-type(3){
            background: #e5d1fd;
        }

        .arco-table-th:nth-of-type(4){
            background: #ffd681;
        }

        .arco-table-th:nth-of-type(5){
            background: #ffd681;
        }

        .arco-table-th:nth-of-type(6){
            background: #8edbe1;
        }

        .arco-table-th:nth-of-type(7){
            background: #fff;
        } 
    } 

    /deep/ .arco-table-tr:nth-of-type(2){
        .arco-table-th:nth-of-type(1),
        .arco-table-th:nth-of-type(2),
        .arco-table-th:nth-of-type(3),
        .arco-table-th:nth-of-type(4),
        .arco-table-th:nth-of-type(5),
        .arco-table-th:nth-of-type(6),
        .arco-table-th:nth-of-type(7){
            background: #ccc;
        }

        .arco-table-th:nth-of-type(8),
        .arco-table-th:nth-of-type(9),
        .arco-table-th:nth-of-type(10),
        .arco-table-th:nth-of-type(11),
        .arco-table-th:nth-of-type(12){
            background: #e5d1fd;
        }

        .arco-table-cell-with-filter{
            color: #333;
        }
    } 

    /deep/ .arco-pagination{
        display: none;
    }
}

.searchFilter{
    padding: 20px;
    background: #fff;
    box-shadow: 0px 5px 15px 0px rgba(0, 0, 0, 0.12), 0px 15px 35px 0px rgba(60, 66, 87, 0.08);
}

.btn_group{
    // float: right;
}
</style>